import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import transportService from '../../services/transportService';
import staffService from '../../services/staffService';
import { Plus, Pencil, Trash, CheckCircle, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import DriverForm from '../../components/transport/DriverForm';
import VerificationModal from '../../components/transport/VerificationModal';
import DialogModal from '../../components/DialogModal';
import './transport.css';

function buildDeleteImpactMessage(impact, noneMessage) {
  let message;
  if (impact.total_to_be_deleted > 0) {
    const deletedLines = Object.entries(impact.will_be_deleted)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message = `This will permanently delete ${impact.total_to_be_deleted} record(s):\n${deletedLines}\n\nThis cannot be undone.`;
  } else {
    message = noneMessage;
  }
  if (Object.keys(impact.will_be_preserved || {}).length > 0) {
    const preservedLines = Object.entries(impact.will_be_preserved)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message += `\n\nThese will be kept, just unlinked:\n${preservedLines}`;
  }
  return message;
}

function DriversManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [driverToVerify, setDriverToVerify] = useState(null);
  const [filters, setFilters] = useState({ role: '', is_active: 'true' });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, driverId: null, message: '', loading: false });

  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'school_admin') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await staffService.listStaff({ limit: 100 });
        const staffData = Array.isArray(response) ? response : response.items || response.staff || [];
        setStaff(staffData);
      } catch (error) {
        console.error('Failed to load staff');
      }
    };
    loadStaff();
  }, []);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        setLoading(true);
        const params = {
          skip: pagination.skip,
          limit: pagination.limit,
          ...(filters.role && { role: filters.role }),
          ...(filters.is_active !== '' && { is_active: filters.is_active === 'true' })
        };
        console.log('Fetching drivers with params:', params);
        console.log('Filters state:', filters);
        const data = await transportService.listDrivers(params);
        console.log('Response data:', data);
        setDrivers(Array.isArray(data) ? data : data.drivers || []);
      } catch (error) {
        console.error('Failed to load drivers:', error);
        toast.error('Failed to load drivers: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDrivers();
  }, [filters, pagination]);

  const handleCreate = async (formData) => {
    try {
      await transportService.createDriver(formData);
      toast.success('Driver registered successfully');
      setShowModal(false);
      setSelectedDriver(null);
      const data = await transportService.listDrivers(pagination);
      setDrivers(Array.isArray(data) ? data : data.drivers || []);
    } catch (error) {
      toast.error('Failed to register driver: ' + error.message);
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await transportService.updateDriver(id, formData);
      toast.success('Driver updated successfully');
      setShowModal(false);
      setSelectedDriver(null);
      const data = await transportService.listDrivers(pagination);
      setDrivers(Array.isArray(data) ? data : data.drivers || []);
    } catch (error) {
      toast.error('Failed to update driver: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    setDeleteDialog({ isOpen: true, driverId: id, message: 'Checking what this will affect...', loading: true });
    try {
      const impact = await transportService.getDriverDeleteImpact(id);
      const message = buildDeleteImpactMessage(impact, 'No live location records are tied to this driver yet — safe to delete.');
      setDeleteDialog({ isOpen: true, driverId: id, message, loading: false });
    } catch (error) {
      console.error('Error fetching delete impact:', error);
      setDeleteDialog({ isOpen: true, driverId: id, message: 'Delete this driver? This cannot be undone.', loading: false });
    }
  };

  const confirmDeleteDriver = async () => {
    const id = deleteDialog.driverId;
    setDeleteDialog({ isOpen: false, driverId: null, message: '', loading: false });
    try {
      await transportService.deleteDriver(id);
      toast.success('Driver removed successfully');
      const data = await transportService.listDrivers(pagination);
      setDrivers(Array.isArray(data) ? data : data.drivers || []);
    } catch (error) {
      toast.error('Failed to delete driver: ' + error.message);
    }
  };

  const handleOpenVerification = (driver) => {
    const staffName = getStaffName(driver.staff_id);
    setDriverToVerify({
      ...driver,
      staff_name: staffName
    });
    setShowVerificationModal(true);
  };

  const handleVerified = (updatedDriver) => {
    const updatedDrivers = drivers.map(d =>
      d.id === updatedDriver.id ? updatedDriver : d
    );
    setDrivers(updatedDrivers);
    setShowVerificationModal(false);
    setDriverToVerify(null);
  };

  const isLicenseExpiring = (expiry) => {
    if (!expiry) return false;
    const days = Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
    return days < 30 && days >= 0;
  };

  const isLicenseExpired = (expiry) => {
    if (!expiry) return false;
    return new Date(expiry) < new Date();
  };

  const getStaffName = (staff_id) => {
    const staffMember = staff.find(s => s.id === staff_id);
    return staffMember ? `${staffMember.first_name} ${staffMember.last_name}` : `Staff ${staff_id?.slice(0, 8) || 'N/A'}`;
  };

  if (loading) {
    return <Layout><div className="loading">Loading drivers...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page drivers-management">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/transport')} title="Back to Transport">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Drivers & Conductors</h1>
              <p>Manage driver and conductor staff</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => { setSelectedDriver(null); setShowModal(true); }}>
            <Plus size={18} /> Register Driver
          </button>
        </div>

        <div className="filters-section">
          <div className="form-group">
            <label>Role</label>
            <select
              value={filters.role}
              onChange={(e) => { setFilters({ ...filters, role: e.target.value }); setPagination({ ...pagination, skip: 0 }); }}
              className="form-control"
            >
              <option value="">All Roles</option>
              <option value="driver">Driver</option>
              <option value="conductor">Conductor</option>
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={filters.is_active}
              onChange={(e) => { setFilters({ ...filters, is_active: e.target.value }); setPagination({ ...pagination, skip: 0 }); }}
              className="form-control"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        <div className="table-section">
          {drivers.length === 0 ? (
            <div className="empty-state">
              <p>No drivers found. Register your first driver.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>License Number</th>
                  <th>License Expiry</th>
                  <th>Role</th>
                  <th>Insurance Expiry</th>
                  <th>Verified</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map(driver => (
                  <tr key={driver.id} className={isLicenseExpired(driver.license_expiry) ? 'row-expired' : ''}>
                    <td><strong>{getStaffName(driver.staff_id)}</strong></td>
                    <td>
                      <span className={isLicenseExpired(driver.license_expiry) ? 'text-danger' : isLicenseExpiring(driver.license_expiry) ? 'text-warning' : ''}>
                        {driver.license_number}
                      </span>
                    </td>
                    <td>
                      <span className={isLicenseExpired(driver.license_expiry) ? 'text-danger' : isLicenseExpiring(driver.license_expiry) ? 'text-warning' : ''}>
                        {driver.license_expiry}
                      </span>
                    </td>
                    <td><span className="badge badge-info">{driver.role}</span></td>
                    <td>{driver.insurance_expiry || '-'}</td>
                    <td>
                      {driver.is_verified ? (
                        <span className="text-success"><CheckCircle size={18} /> Verified</span>
                      ) : (
                        <span className="text-muted">Pending</span>
                      )}
                    </td>
                    <td><span className={`badge ${driver.is_active ? 'badge-success' : 'badge-danger'}`}>{driver.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td className="actions">
                      {!driver.is_verified && (
                        <button
                          className="btn-icon btn-success"
                          onClick={() => handleOpenVerification(driver)}
                          title="Verify Driver"
                        >
                          <ShieldCheck size={18} />
                        </button>
                      )}
                      <button className="btn-icon" onClick={() => { setSelectedDriver(driver); setShowModal(true); }} title="Edit">
                        <Pencil size={18} />
                      </button>
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(driver.id)} title="Delete">
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pagination">
          <button disabled={pagination.skip === 0} onClick={() => setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) })}>
            Previous
          </button>
          <span>Page {Math.floor(pagination.skip / pagination.limit) + 1}</span>
          <button disabled={drivers.length < pagination.limit} onClick={() => setPagination({ ...pagination, skip: pagination.skip + pagination.limit })}>
            Next
          </button>
        </div>

        {showModal && (
          <DriverForm
            driver={selectedDriver}
            onSave={selectedDriver ? (id, data) => handleUpdate(id, data) : (_, data) => handleCreate(data)}
            onClose={() => { setShowModal(false); setSelectedDriver(null); }}
          />
        )}

        {showVerificationModal && driverToVerify && (
          <VerificationModal
            driver={driverToVerify}
            onVerified={handleVerified}
            onClose={() => { setShowVerificationModal(false); setDriverToVerify(null); }}
          />
        )}
      </div>

      <DialogModal
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, driverId: null, message: '', loading: false })}
        title="Delete Driver"
        message={deleteDialog.message}
        type="warning"
        isLoading={deleteDialog.loading}
        disableCloseWhenLoading={false}
        primaryAction={deleteDialog.loading ? null : { label: 'Delete', onClick: confirmDeleteDriver }}
        secondaryAction={deleteDialog.loading ? null : { label: 'Cancel', onClick: () => {} }}
      />
    </Layout>
  );
}

export default DriversManagementPage;
